import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from "vue";

export interface FieldSuggestion {
  suggested: string | null;
  message: string;
  /** Optional i18n key for the clickable link text (e.g. "whitespaceFix"). When set, the link shows this translated text instead of the suggested value. */
  linkMessage?: string;
}

/**
 * A suggestion rule that tests a field value and optionally returns a suggestion.
 * Rules are evaluated in order: the first match wins.
 */
export interface SuggestionRule {
  /** Test whether this rule applies to the given input value. */
  test: (value: string) => boolean;
  /** Extract the suggested value from the input. Return null to skip. */
  suggest: (value: string) => string | null;
  /** i18n message key (under pages.wizard.steps.settings.suggestions) */
  message: string;
}

const DEBOUNCE_MS = 500;

// The first rule that has something to say about a value: a fix, or a
// warning with none (suggested null).
function firstMatch(raw: string, rules: SuggestionRule[]): { rule: SuggestionRule; suggested: string | null } | null {
  for (const rule of rules) {
    if (!rule.test(raw)) continue;
    const suggested = rule.suggest(raw);
    if (suggested === null) return { rule, suggested: null };
    if (suggested && suggested !== raw) return { rule, suggested };
  }
  return null;
}

// How many more times a fix is put back through its rules.
const MAX_FOLLOW_ONS = 4;

// A fix that another rule in the set would then fix again offers the admin a
// value only to tell them it is wrong once they take it: https://salad becomes
// salad, then salad:9999. So the fix is put back through the rules until none
// changes it, and the end of that chain is offered, under the first rule's
// message, since that names what was wrong with what they typed.
//
// This is a fallback. Each rule should hand back a value its own set already
// accepts (socketUrlRule writes host:port, not just host), and "settles in one
// step" in the spec fails a rule set that leans on this.
function evaluateRules(raw: string, rules: SuggestionRule[]): FieldSuggestion | null {
  const first = firstMatch(raw, rules);
  if (!first) return null;
  if (first.suggested === null) return { suggested: null, message: first.rule.message };
  let value = first.suggested;
  const seen = new Set([raw, value]);
  for (let step = 0; step < MAX_FOLLOW_ONS; step++) {
    const next = firstMatch(value, rules);
    // A warning with no fix leaves the last fix standing, and a value already
    // seen is two rules undoing each other.
    if (!next || next.suggested === null || seen.has(next.suggested)) break;
    value = next.suggested;
    seen.add(value);
  }
  return { suggested: value, message: first.rule.message };
}

/**
 * Evaluates a set of suggestion rules against a reactive field value.
 * Returns the first matching suggestion, or null.
 * Debounces changes so suggestions don't flicker while the user is typing.
 */
export function useFieldSuggestion(value: Ref<string>, ruleSource: MaybeRefOrGetter<SuggestionRule[]>) {
  const suggestion = ref<FieldSuggestion | null>(null);
  let timer: ReturnType<typeof setTimeout> | null = null;

  // The rules can change under a field (a proxy address only takes the host
  // rule over tcp), so they are read, and watched, alongside the value.
  watch(
    [() => value.value, () => toValue(ruleSource)],
    ([raw, rules]) => {
      if (timer) clearTimeout(timer);
      const trimmed = raw?.trim();

      // Clear immediately when empty
      if (!trimmed) {
        suggestion.value = null;
        return;
      }

      const hasWhitespace = raw !== trimmed;

      // Evaluate rules on the trimmed value so whitespace doesn't interfere
      const result = evaluateRules(trimmed, rules);

      // When a rule produces an actionable fix, prefer it over a whitespace-only
      // suggestion. The fix is already based on the trimmed value so whitespace
      // is removed automatically.
      if (result?.suggested) {
        timer = setTimeout(() => {
          suggestion.value = evaluateRules(trimmed, rules);
        }, DEBOUNCE_MS);
        return;
      }

      // No actionable rule fix: fall back to whitespace suggestion if needed
      if (hasWhitespace) {
        timer = setTimeout(() => {
          suggestion.value = { suggested: trimmed, message: "whitespace", linkMessage: "whitespaceFix" };
        }, DEBOUNCE_MS);
        return;
      }

      // Clear immediately when input is valid (no suggestion needed)
      if (!result) {
        suggestion.value = null;
        return;
      }

      // Debounce showing warning-only suggestions (suggested is null)
      timer = setTimeout(() => {
        suggestion.value = result;
      }, DEBOUNCE_MS);
    },
    { immediate: true },
  );

  return { suggestion: computed(() => suggestion.value) };
}

// ---------------------------------------------------------------------------
// Reusable rule builders
// ---------------------------------------------------------------------------

/**
 * Detects a URL with path, query, or fragment and suggests stripping to just
 * scheme + host (+ port). For fields that expect a base URL like `https://1.2.3.4`.
 */
export function urlHasExtraPathRule(): SuggestionRule {
  return {
    test: (value) => {
      try {
        const u = new URL(value);
        if (u.protocol !== "http:" && u.protocol !== "https:") return false;
        return u.pathname !== "/" || u.search !== "" || u.hash !== "";
      } catch {
        return false;
      }
    },
    suggest: (value) => {
      const u = new URL(value);
      return `${u.protocol}//${u.host}`;
    },
    message: "urlHasExtraPath",
  };
}

/**
 * Detects a URL with a query string or fragment and suggests stripping them
 * while preserving the path. A trailing slash on the path is also removed.
 * For fields where a sub-path is legitimate but query/fragment is not, e.g.
 * the server's public URL (`https://host/sub` is fine, `?token=` is not).
 */
export function urlHasQueryOrFragmentRule(): SuggestionRule {
  return {
    test: (value) => {
      try {
        const u = new URL(value);
        if (u.protocol !== "http:" && u.protocol !== "https:") return false;
        return u.search !== "" || u.hash !== "";
      } catch {
        return false;
      }
    },
    suggest: (value) => {
      const u = new URL(value);
      return `${u.origin}${u.pathname.replace(/\/+$/, "")}`;
    },
    message: "urlHasQueryOrFragment",
  };
}

/**
 * Detects a bare hostname or IP (no scheme) and suggests prepending `https://`.
 * Also handles cases like `host.com/path` by stripping the path portion.
 * For fields that expect a full base URL.
 */
export function missingSchemeRule(): SuggestionRule {
  return {
    test: (value) => {
      if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(value)) return false;
      const hostPart = value.split(/[/?#]/)[0].replace(/:$/, "");
      return looksLikeHostnameOrIp(hostPart);
    },
    suggest: (value) => {
      const hostPart = value.split(/[/?#]/)[0].replace(/:$/, "");
      return `https://${hostPart}`;
    },
    message: "missingScheme",
  };
}

/**
 * Like missingSchemeRule but preserves the path: for fields where a base URL
 * may legitimately carry a sub-path (e.g. the server's public URL). Suggests
 * prepending `https://` to a scheme-less value, keeping the path after the host
 * and dropping what publicUrlRules would only strip next (a query, a fragment,
 * a trailing slash or colon), so the fix is offered whole.
 */
export function missingSchemeKeepPathRule(): SuggestionRule {
  return {
    test: (value) => {
      if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(value)) return false;
      const hostPart = value.split(/[/?#]/)[0].replace(/:$/, "");
      return looksLikeHostnameOrIp(hostPart);
    },
    suggest: (value) => {
      try {
        const u = new URL(`https://${value}`);
        return `${u.origin}${u.pathname.replace(/\/+$/, "")}`;
      } catch {
        return `https://${value}`;
      }
    },
    message: "missingScheme",
  };
}

/**
 * Detects a full URL pasted into a field that expects just a hostname or IP.
 * Suggests extracting the host portion.
 */
export function urlInHostnameFieldRule(): SuggestionRule {
  return {
    test: (value) => {
      try {
        const u = new URL(value);
        return u.protocol === "http:" || u.protocol === "https:";
      } catch {
        return false;
      }
    },
    suggest: (value) => {
      const u = new URL(value);
      return u.host;
    },
    message: "urlInHostnameField",
  };
}

/**
 * Detects a URL with a trailing colon but no port number (e.g. `https://127.0.0.1:`)
 * and suggests removing the colon.
 */
export function trailingColonRule(): SuggestionRule {
  return {
    test: (value) => /:$/.test(value) && /^https?:\/\/.+:$/.test(value),
    suggest: (value) => value.replace(/:$/, ""),
    message: "trailingColon",
  };
}

/**
 * Detects a value with a trailing slash and suggests removing it.
 * Useful as a low-priority cleanup rule.
 */
export function trailingSlashRule(): SuggestionRule {
  return {
    test: (value) => value.endsWith("/") && value.length > 1,
    suggest: (value) => value.replace(/\/+$/, ""),
    message: "trailingSlash",
  };
}

/**
 * Catch-all rule: warns if the value doesn't look like a valid base URL.
 * Expected format: http(s)://host[:port] where host is an IP, FQDN, or bracketed IPv6.
 * Place last in a rule set: earlier rules provide actionable suggestions first,
 * this catches anything else that slipped through.
 */
export function notABaseUrlRule(): SuggestionRule {
  return {
    test: (value) => !looksLikeBaseUrl(value),
    suggest: (value) => {
      // Try to extract a usable base URL from the value
      try {
        const u = new URL(value);
        if (u.protocol === "http:" || u.protocol === "https:") {
          const base = `${u.protocol}//${u.host}`;
          if (base !== value) return base;
        }
      } catch {
        // Not parseable: no actionable suggestion
      }
      return null;
    },
    message: "unusualValue",
  };
}

/**
 * Detects a base64-encoded string with incorrect padding and suggests fixing it.
 * Valid base64 has length divisible by 4, with at most 2 trailing `=` chars.
 * Users commonly miss trailing `=` when copy-pasting API keys, or grab too many.
 *
 * Note: some tokens (e.g. PAN-OS API keys) embed `=` inside the content, so we
 * allow `=` anywhere, not just at the end.
 */
export function base64PaddingRule(): SuggestionRule {
  return {
    test: (value) => {
      if (!/^[A-Za-z0-9+/=]+$/.test(value)) return false;
      if (value.length < 4) return false;
      const trailingEq = value.length - value.replace(/=+$/, "").length;
      return value.length % 4 !== 0 || trailingEq > 2;
    },
    suggest: (value) => {
      const base = value.replace(/=+$/, "");
      const remainder = base.length % 4;
      // Remainder 0: no padding needed, just strip excess =
      if (remainder === 0) return base;
      // Remainder 2 or 3: add correct padding
      if (remainder === 2) return base + "==";
      if (remainder === 3) return base + "=";
      // Remainder 1: ambiguous. Some trailing = were likely content.
      // Restore one = as content, then pad the result.
      return base + "===";
    },
    message: "base64Padding",
  };
}

/**
 * Catch-all rule: warns if the value doesn't look like valid base64.
 * Place last in a rule set: earlier rules provide actionable suggestions first.
 */
export function notBase64Rule(): SuggestionRule {
  return {
    test: (value) => !/^[A-Za-z0-9+/=]+$/.test(value) || value.length < 4,
    suggest: () => null,
    message: "unusualBase64",
  };
}

// ---------------------------------------------------------------------------
// Pre-composed rule sets for common field types
// ---------------------------------------------------------------------------

/** For Hostname fields that expect `https://host[:port]` (Fortinet, PanOS, Firepower, etc.) */
export const baseUrlRules: SuggestionRule[] = [
  urlHasExtraPathRule(),
  missingSchemeRule(),
  trailingColonRule(),
  notABaseUrlRule(),
];

/**
 * For a base URL that may carry a sub-path (the server's public URL). Unlike
 * baseUrlRules it never suggests stripping the path, only the scheme, query,
 * fragment and trailing slash are cleaned up.
 */
export const publicUrlRules: SuggestionRule[] = [
  missingSchemeKeepPathRule(),
  urlHasQueryOrFragmentRule(),
  trailingColonRule(),
  trailingSlashRule(),
];

/** For fields that expect a bare hostname or IP */
export const hostnameRules: SuggestionRule[] = [urlInHostnameFieldRule()];

/**
 * A URL pasted where a socket address goes (`tcp://haproxy:9999`, or a stats
 * page's `https://haproxy`): offers host:port in one step, the URL's own port
 * when it names one and the proxies' usual one when it does not, rather than
 * the bare host for socketMissingPortRule to fix a second time. The port is read
 * off the text, since URL() drops one it thinks is the scheme's default
 * (`https://haproxy:443`). A `unix://` URL offers its path, which SocketField
 * then reads as a unix socket.
 */
export function socketUrlRule(port = 9999): SuggestionRule {
  const URL_PREFIX = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/(\[[^\]]+\]|[^/:?#\s]*)(?::(\d+))?([^?#]*)/;
  return {
    test: (value) => URL_PREFIX.test(value),
    suggest: (value) => {
      const [, scheme, host, givenPort, path] = URL_PREFIX.exec(value) ?? [];
      if (scheme?.toLowerCase() === "unix") return path?.startsWith("/") ? path : null;
      if (!host) return null;
      return `${host}:${givenPort ?? port}`;
    },
    message: "urlInSocketField",
  };
}

/**
 * A TCP admin socket is dialled as host:port, and a bare host fails at connect
 * time on the agent ("missing port in address"). Offers the port the proxies'
 * own examples use. Only a plain hostname or IPv4 address: an IPv6 literal
 * already carries colons, and anything with a path in it is not a host at all.
 */
export function socketMissingPortRule(port = 9999): SuggestionRule {
  return {
    test: (value) => /^[A-Za-z0-9.-]+$/.test(value),
    suggest: (value) => `${value}:${port}`,
    message: "socketMissingPort",
  };
}

export const tcpSocketRules: SuggestionRule[] = [socketUrlRule(), socketMissingPortRule()];

/** For fields that expect a base64-encoded value (e.g. API keys) */
export const base64Rules: SuggestionRule[] = [base64PaddingRule(), notBase64Rule()];

/**
 * Cisco Meraki Dashboard API keys are 40-character hex, not base64. Soft-warn on
 * anything else with no autofix. The base64 padding fix is meaningless for a hex
 * key, so this only ever shows the "doesn't look like a valid API key" message.
 */
export function notMerakiApiKeyRule(): SuggestionRule {
  return {
    test: (value) => !/^[0-9a-fA-F]{40}$/.test(value),
    suggest: () => null,
    message: "unusualBase64",
  };
}

/** For hex API-key fields (e.g. Cisco Meraki), soft warning only, no padding autofix. */
export const merakiApiKeyRules: SuggestionRule[] = [notMerakiApiKeyRule()];

/**
 * Cisco Meraki organisation and policy-object-group IDs are numeric. Soft-warn on
 * anything else (no autofix). This catches the common mistake of pasting the
 * dashboard URL slug (e.g. C_1234) instead of the numeric ID. Whitespace on paste
 * is handled separately by the built-in whitespace rule.
 */
export function notNumericIdRule(): SuggestionRule {
  return {
    test: (value) => !/^[0-9]+$/.test(value),
    suggest: () => null,
    message: "unusualId",
  };
}

/** For numeric-ID fields (e.g. Cisco Meraki organisation / policy-object-group IDs). */
export const numericIdRules: SuggestionRule[] = [notNumericIdRule()];

/**
 * Fastly API tokens are URL-safe base64 (letters, digits, - and _), so
 * base64Rules would offer a padding fix that means nothing here. Soft-warn only.
 */
export function notFastlyApiTokenRule(): SuggestionRule {
  return {
    test: (value) => !/^[A-Za-z0-9_-]{20,64}$/.test(value),
    suggest: () => null,
    message: "unusualBase64",
  };
}

/** For the Fastly API token field, soft warning only, no autofix. */
export const fastlyApiTokenRules: SuggestionRule[] = [notFastlyApiTokenRule()];

/**
 * Fastly service IDs are alphanumeric (22 characters today). Soft-warn on
 * anything else, which catches pasting a service name or a dashboard URL.
 */
export function notFastlyServiceIdRule(): SuggestionRule {
  return {
    test: (value) => !/^[A-Za-z0-9]{6,64}$/.test(value),
    suggest: () => null,
    message: "unusualFastlyServiceId",
  };
}

/** For the Fastly service ID field, soft warning only, no autofix. */
export const fastlyServiceIdRules: SuggestionRule[] = [notFastlyServiceIdRule()];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if value matches the expected base URL format:
 * http(s)://host[:port] where host is an IPv4, FQDN (with dots), or bracketed IPv6.
 */
function looksLikeBaseUrl(value: string): boolean {
  try {
    const u = new URL(value);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    // Must have no path (beyond /), query, or fragment
    if (u.pathname !== "/" || u.search !== "" || u.hash !== "") return false;
    // Hostname must be a valid IP, hostname (a short internal one included,
    // since the scheme is already there) or bracketed IPv6. Use host rather
    // than hostname: it carries the port, which the check handles.
    return looksLikeHostnameOrIp(u.host, { allowSingleLabel: true });
  } catch {
    return false;
  }
}

// Whether a host looks like something the admin meant. `allowSingleLabel`
// splits the two questions that used to share one answer:
//
//   false (the default, for a scheme-less string): "is this a host I should
//   offer to prefix with https://". A bare word is far more often a stray
//   paste than a hostname, and answering yes turns "salad" into a "did you
//   mean https://salad" prompt, so a dot is required here.
//
//   true (for the host of a value that already parsed as a URL): "is this
//   host acceptable". An appliance on the admin's own network is reached by
//   the short name their resolver completes (`fw01`, `panorama`, `edge-fw`),
//   which is the ordinary case for this product and used to warn on every one
//   of them. The scheme is the admin saying they meant a host, and there is
//   nothing to tell `https://panorama` apart from a host we have never heard
//   of, so it is accepted.
function looksLikeHostnameOrIp(value: string, { allowSingleLabel = false } = {}): boolean {
  // Strip optional port suffix for the checks below
  const withoutPort = value.replace(/:\d+$/, "");
  // Bare IPv4 (exactly 4 octets)
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(withoutPort)) return true;
  // Reject all-numeric labels: looks like a malformed IP, not a hostname
  if (/^\d+(\.\d+)*$/.test(withoutPort)) return false;
  // localhost (with optional port). Kept for the single-label-rejecting caller;
  // the other one admits it through the hostname pattern below.
  if (withoutPort === "localhost") return true;
  // Hostname with optional port, dotted unless single labels are allowed.
  const label = "[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?";
  const dots = allowSingleLabel ? "*" : "{1,}";
  if (new RegExp(`^${label}(\\.${label})${dots}(:\\d+)?$`).test(value)) return true;
  // Bracketed IPv6 with optional port
  if (/^\[[\da-fA-F:]+\](:\d+)?$/.test(value)) return true;
  return false;
}

/**
 * Vendor-generated UUID fields (Cisco FMC network groups, DigitalOcean
 * firewalls). The id only ever comes back from the vendor's API, so anything
 * that is not a UUID is a paste of the wrong thing. Soft warning, no autofix.
 */
export function notUuidRule(): SuggestionRule {
  return {
    test: (value) => !/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value),
    suggest: () => null,
    message: "unusualUuid",
  };
}

/** For fields holding a vendor-generated UUID. */
export const uuidRules: SuggestionRule[] = [notUuidRule()];

/**
 * Okta network zone ids are prefixed ids like `nzo137hn19nG7fb3A698`. The
 * common mistake is pasting the zone's display name instead. Soft warning.
 */
export function notOktaZoneIdRule(): SuggestionRule {
  return {
    test: (value) => !/^nzo[0-9a-zA-Z]+$/.test(value),
    suggest: () => null,
    message: "unusualOktaZoneId",
  };
}

/** For the Okta network zone id field. */
export const oktaZoneIdRules: SuggestionRule[] = [notOktaZoneIdRule()];
